import type { ContentKind } from "./content-kinds"

/**
 * Formato de design: contrato geométrico entre template e produto.
 *
 * Em impressão, as medidas são em milímetros e incluem sangria (bleed) e área
 * segura (safe). No editor, o frame é convertido para pixels de edição
 * (`PX_PER_MM`); o render multiplica pelo DPI de saída.
 */
export interface DesignFormat {
  id: string
  name: string
  kind: ContentKind
  width: number
  height: number
  unit: "mm" | "px"
  bleed: number
  safe: number
  dpi: number
  pages: number
}

/** Resolução de edição: 6 px por mm (~152 dpi). O render usa `dpi`. */
export const PX_PER_MM = 6

export const PRINT_FORMATS: DesignFormat[] = [
  {
    id: "card-90x50",
    name: "Cartão de visita",
    kind: "PRINT",
    width: 90,
    height: 50,
    unit: "mm",
    bleed: 3,
    safe: 4,
    dpi: 300,
    pages: 2,
  },
  {
    id: "flyer-a5",
    name: "Panfleto A5",
    kind: "PRINT",
    width: 148,
    height: 210,
    unit: "mm",
    bleed: 3,
    safe: 5,
    dpi: 300,
    pages: 2,
  },
  {
    id: "leaflet-a4",
    name: "Folheto A4",
    kind: "PRINT",
    width: 210,
    height: 297,
    unit: "mm",
    bleed: 3,
    safe: 5,
    dpi: 300,
    pages: 2,
  },
  {
    id: "poster-a4",
    name: "Cartaz A4",
    kind: "PRINT",
    width: 210,
    height: 297,
    unit: "mm",
    bleed: 3,
    safe: 5,
    dpi: 300,
    pages: 1,
  },
  {
    id: "poster-a3",
    name: "Cartaz A3",
    kind: "PRINT",
    width: 297,
    height: 420,
    unit: "mm",
    bleed: 3,
    safe: 5,
    dpi: 300,
    pages: 1,
  },
]

export const DIGITAL_FORMATS: DesignFormat[] = [
  {
    id: "square-1080",
    name: "Post quadrado",
    kind: "DIGITAL",
    width: 1080,
    height: 1080,
    unit: "px",
    bleed: 0,
    safe: 0,
    dpi: 72,
    pages: 1,
  },
  {
    id: "portrait-1080x1350",
    name: "Post vertical",
    kind: "DIGITAL",
    width: 1080,
    height: 1350,
    unit: "px",
    bleed: 0,
    safe: 0,
    dpi: 72,
    pages: 1,
  },
  {
    id: "story-1080x1920",
    name: "Story",
    kind: "DIGITAL",
    width: 1080,
    height: 1920,
    unit: "px",
    bleed: 0,
    safe: 0,
    dpi: 72,
    pages: 1,
  },
  {
    id: "wide-1920x1080",
    name: "Widescreen",
    kind: "DIGITAL",
    width: 1920,
    height: 1080,
    unit: "px",
    bleed: 0,
    safe: 0,
    dpi: 72,
    pages: 1,
  },
  {
    id: "a4-digital",
    name: "A4 digital",
    kind: "DIGITAL",
    width: 1240,
    height: 1754,
    unit: "px",
    bleed: 0,
    safe: 0,
    dpi: 150,
    pages: 1,
  },
]

export const formatsForKind = (kind: ContentKind): DesignFormat[] => {
  if (kind === "PRINT") return PRINT_FORMATS
  if (kind === "DIGITAL") return DIGITAL_FORMATS
  return []
}

/** Converte o formato para as medidas do canvas (px de edição). */
export const formatToFrame = (format: DesignFormat) => ({
  width: format.unit === "mm" ? Math.round(format.width * PX_PER_MM) : format.width,
  height: format.unit === "mm" ? Math.round(format.height * PX_PER_MM) : format.height,
})

/** "90 × 50 mm" ou "1080 × 1080 px". */
export const formatSizeLabel = (format: DesignFormat) =>
  `${format.width} × ${format.height} ${format.unit}`

/** Maior lado do frame, para escolher o zoom inicial. */
export const formatLongSide = (format: DesignFormat) => Math.max(formatToFrame(format).width, formatToFrame(format).height)
